<template>
  <div style="max-width: 960px; margin: 0 auto; padding: 1.25rem;">

    <!-- Toast host. Mounted here and NOT in FoundationCard: vue3-notification
         renders one copy per host, so a host per card would show N copies of
         every toast. There is no host in layouts/app.blade.php.

         Teleported to body because layouts/app.blade.php gives <main> a
         `z-0`, and that stacking context traps the library's position:fixed
         group underneath the mobile sticky header (z-10) — measured: only a
         ~1px sliver of the toast shows at 375px wide. Same trap that forced
         the edit-wizard modals to teleport. -->
    <Teleport to="body">
      <notifications position="top center"></notifications>
    </Teleport>

    <!-- Header -->
    <div style="margin-bottom: 1.25rem;">
      <h1 style="font-size: 1.5rem; font-weight: 700; color: #111827; margin: 0 0 0.25rem;">My Dashboard</h1>
      <p style="font-size: 0.875rem; color: #6B7280; margin: 0;">
        {{ active ? 'Active campaigns' : 'Past campaigns' }}
      </p>
    </div>

    <!-- Action bar -->
    <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1.25rem; padding: 0.875rem 1rem; background: #FAFAFA; border: 1px solid #E5E7EB; border-radius: 0.625rem;">
      <a style="color: #EC4899; text-decoration: none; font-size: 0.875rem; font-weight: 500;" :href="startCampaignLink" @mouseover="$event.target.style.color='#BE185D'" @mouseout="$event.target.style.color='#EC4899'">
        + Start New Campaign
      </a>
      <span style="color: #D1D5DB;">|</span>
      <a href="#" @click.prevent="getCampaigns" style="color: #EC4899; text-decoration: none; font-size: 0.875rem; font-weight: 500;" @mouseover="$event.target.style.color='#BE185D'" @mouseout="$event.target.style.color='#EC4899'">
        {{ campaignTypeText }}
      </a>
      <span style="color: #D1D5DB;">|</span>
      <a href="#campaignData" v-smooth-scroll style="color: #EC4899; text-decoration: none; font-size: 0.875rem; font-weight: 500;" @mouseover="$event.target.style.color='#BE185D'" @mouseout="$event.target.style.color='#EC4899'">
        Campaign Data
      </a>
    </div>

    <!-- Summary stats (admin only) -->
    <div v-if="admin && summary" style="display: flex; gap: 0.75rem; margin-bottom: 1.25rem; flex-wrap: wrap;">
      <div style="flex: 1; min-width: 120px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ summary.totalFoundations }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Programs</div>
      </div>
      <div style="flex: 1; min-width: 120px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ formatNumber(summary.totalCampaigns) }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Campaigns</div>
      </div>
      <div style="flex: 1; min-width: 120px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">${{ formatCurrency(summary.totalRaised) }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Total Raised</div>
      </div>
    </div>

    <!-- Summary stats (non-admin) -->
    <div v-if="!admin && !loading && campaigns.length > 0" style="display: flex; gap: 0.75rem; margin-bottom: 1.25rem; flex-wrap: wrap;">
      <div style="flex: 1; min-width: 120px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ campaigns.length }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">My Campaigns</div>
      </div>
      <div style="flex: 1; min-width: 120px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ totalSupporters }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Supporters</div>
      </div>
      <div style="flex: 1; min-width: 120px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">${{ userTotalRaised }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Total Raised</div>
      </div>
      <div v-if="donations.length > 0" style="flex: 1; min-width: 120px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ donations.length }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Donations</div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" style="text-align: center; padding: 3rem; color: #9CA3AF;">
      <div style="display: inline-block; width: 2rem; height: 2rem; border: 3px solid #E5E7EB; border-top-color: #EC4899; border-radius: 50%; animation: spin 0.8s linear infinite;"></div>
      <p style="margin-top: 0.75rem; font-size: 0.875rem;">Loading campaigns...</p>
    </div>

    <!-- Admin view: Foundation-grouped hierarchy -->
    <div v-else-if="admin" style="display: flex; flex-direction: column; gap: 1rem;">
      <foundation-card
        v-for="foundation in campaigns"
        :key="foundation.id"
        :foundation="foundation"
        :can-archive="canArchive"
        @archived="onArchived"
      ></foundation-card>
    </div>

    <!-- Non-admin view: User campaigns grouped by foundation -->
    <div v-else style="display: flex; flex-direction: column; gap: 1rem;">
      <div v-for="(group, foundationId) in groupedCampaigns" :key="foundationId">
        <!-- Foundation group header (only if multiple groups) -->
        <div v-if="Object.keys(groupedCampaigns).length > 1" style="padding: 0.5rem 0.75rem; margin-bottom: 0.5rem; background: #F3F4F6; border-radius: 0.5rem; display: flex; align-items: center; gap: 0.5rem;">
          <div style="width: 0.375rem; height: 0.375rem; border-radius: 50%; background: #EC4899;"></div>
          <span style="font-size: 0.8125rem; font-weight: 600; color: #374151;">{{ foundationNames[foundationId] || 'My Campaigns' }}</span>
          <span style="font-size: 0.6875rem; color: #9CA3AF; margin-left: auto;">{{ group.length }} campaign{{ group.length !== 1 ? 's' : '' }}</span>
        </div>

        <!-- Campaign cards -->
        <div style="display: flex; flex-direction: column; gap: 0.75rem;">
          <user-campaign-card
            v-for="campaign in group"
            :key="campaign.id"
            :campaign="campaign"
            :children="childrenMap[campaign.id] || []"
          ></user-campaign-card>
        </div>
      </div>
    </div>

    <!-- Donations table -->
    <div v-if="donations.length > 0" style="margin-top: 2rem;">
      <h3 style="font-size: 1.125rem; font-weight: 600; color: #111827; text-align: center; margin-bottom: 1rem;">Recent Donations</h3>
      <div class="hidden sm:block" style="border: 1px solid #E5E7EB; border-radius: 0.625rem; overflow: hidden;">
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background: #F9FAFB;">
              <th style="padding: 0.75rem 1rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #E5E7EB;">Donor</th>
              <th style="padding: 0.75rem 1rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #E5E7EB;">Amount</th>
              <th style="padding: 0.75rem 1rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #E5E7EB;">Campaign</th>
              <th style="padding: 0.75rem 1rem; text-align: left; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #E5E7EB;">Date</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="donation in donations" :key="donation.id" style="border-bottom: 1px solid #F3F4F6;" @mouseover="$event.currentTarget.style.background='#F9FAFB'" @mouseout="$event.currentTarget.style.background='transparent'">
              <td style="padding: 0.75rem 1rem; font-size: 0.875rem; color: #374151;">{{ donorName(donation) }}</td>
              <td style="padding: 0.75rem 1rem; font-size: 0.875rem; color: #374151;">{{ donation.amount }}</td>
              <td style="padding: 0.75rem 1rem; font-size: 0.875rem; color: #374151;">{{ donation.campaign?.name }}</td>
              <td style="padding: 0.75rem 1rem; font-size: 0.875rem; color: #9CA3AF;">{{ donation.created_at }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <campaign-stats v-if="active" id="campaignData"/>

  </div>
</template>

<script>
import FoundationCard from "./FoundationCard.vue";
import UserCampaignCard from "./UserCampaignCard.vue";
// Relative, not "@/": vitest.config.js defines an @ alias that webpack.mix.js
// does not, so an @ import passes test:unit and dies at `npm run prod`.
import { buildHttpErrorNotification } from "../../http-error-notifier";

export default {
  components: {
    FoundationCard,
    UserCampaignCard,
  },
  props: {
    // Which list to show on mount. The "Past Campaigns" nav link used to render
    // the ACTIVE list because this was hardcoded true in data().
    initialActive: { type: Boolean, default: true },
  },
  data() {
    return {
      admin: false,
      canArchive: false,
      campaigns: [],
      donations: [],
      active: this.initialActive,
      loading: true,
      summary: null,
      foundationNames: {},
      childrenMap: {},
    }
  },
  computed: {
    startCampaignLink() {
      return this.admin ? '/admin/create-annual-campaign' : 'https://side-out.org/start-a-fundraiser/';
    },
    campaignTypeText() {
      return this.active ? 'View Past Campaigns' : 'View Active Campaigns';
    },
    groupedCampaigns() {
      if (this.admin) return {};
      const grouped = {};
      this.campaigns.forEach(c => {
        const fId = c.foundation_id || 'other';
        if (!grouped[fId]) grouped[fId] = [];
        grouped[fId].push(c);
      });
      return grouped;
    },
    totalSupporters() {
      if (!this.campaigns) return 0;
      return this.campaigns.reduce((sum, c) => sum + (c.children_node_count || 0), 0);
    },
    userTotalRaised() {
      if (!this.campaigns) return '0';
      const total = this.campaigns.reduce((sum, c) => sum + (parseFloat(c.total_raised_with_children_campaigns) || 0), 0);
      return total.toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
  },
  created() {
    this.fetchCampaigns();
  },
  methods: {
    donorName(donation) {
      // This authenticated table shows contact names, including anonymous and
      // business donors. fullName is an accessor but is not serialized by the API.
      return [donation.first_name, donation.last_name]
        .filter(name => typeof name === 'string' && name.length > 0)
        .join(' ');
    },
    fetchCampaigns() {
      this.loading = true;
      const link = this.active ? '/get-active-campaigns' : '/get-inactive-campaigns';
      axios.get(link).then(response => {
        this.campaigns = response.data.campaigns;
        this.donations = response.data.donations || [];
        this.admin = response.data.admin;
        this.canArchive = !!response.data.canArchive;
        this.summary = response.data.summary || null;
        this.foundationNames = response.data.foundationNames || {};
        this.childrenMap = response.data.childrenMap || {};
        this.loading = false;
      }).catch(error => {
        this.loading = false;
        // Never swallow this: a bare catch here is what made the 403 in #228
        // look like "the button does nothing".
        this.$notify(buildHttpErrorNotification(error));
      });
    },
    getCampaigns() {
      this.active = !this.active;
      this.fetchCampaigns();
    },
    // Drop the card locally rather than re-fetching: the archive just forgot
    // active_campaigns_dashboard, so an immediate refetch would hit the cold
    // path and pay a full synchronous rebuild.
    onArchived(id) {
      const gone = this.campaigns.find(c => String(c.id) === String(id));
      this.campaigns = this.campaigns.filter(c => String(c.id) !== String(id));

      // Keep the tiles honest. They are built as (roots) and
      // (roots + their DIRECT children) in WarmActiveCampaignsDashboardJob,
      // so subtracting in those same units is exact — no refetch needed, which
      // matters because the archive just forgot active_campaigns_dashboard and
      // a refetch would pay a synchronous cold rebuild.
      if (gone && this.summary) {
        this.summary = {
          ...this.summary,
          totalFoundations: Math.max(0, (this.summary.totalFoundations || 0) - 1),
          totalCampaigns: Math.max(0, (this.summary.totalCampaigns || 0) - 1 - (gone.direct_child_count || 0)),
          totalRaised: Math.max(0, (this.summary.totalRaised || 0) - (parseFloat(gone.total_raised_with_children_campaigns) || 0)),
        };
      }
    },
    formatNumber(n) {
      return n ? n.toLocaleString() : '0';
    },
    formatCurrency(dollars) {
      if (!dollars) return '0';
      return Number(dollars).toLocaleString(undefined, { maximumFractionDigits: 0 });
    },
  },
}
</script>

<style>
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
